<script lang="ts" setup>
import { i18n } from '@/i18n';
import InfoModal from '@/corePagesModule/components/InfoModal.vue';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { FetchExcelModal } from '@/registerInputModule/pages/interfaces/translateExcelStore';
import { ref, watch } from 'vue';
import { DISPLAY_FORECAST_PERIOD } from '@/registerInputModule/constants/importExcelConstants';

const t = i18n.t;

const props = defineProps<{
  state: FetchExcelModal;
}>();

const emits = defineEmits<{
  (e: 'onNext'): void;
}>();

// YYYYMMの文字列を作成
const monthTexts = Array.from({ length: DISPLAY_FORECAST_PERIOD }, (_, num) =>
  DateService.convertIYearMonthToYYYYMMWithSlash(DateService.getShiftedIYearMonthFromNowDateByMonths(num), true)
);

const maxTableNumberColumnWidthValue = ref('');
const maxTableSupplierNameColumnWidthValue = ref('');

/**
 * テキストが省略されないための最小幅を計算する関数
 * @param text - テキスト
 * @returns 計算結果の最小幅（ピクセル）
 */
const calculateMinWidth = (text: string): number => {
  // テキストがなければ 0 を返す
  if (!text) return 0;

  // 仮要素を作り、実際の文字幅を取得する
  // modal__table__cellを適用する
  const hiddenElement = document.createElement('span');
  hiddenElement.style.position = 'absolute';
  hiddenElement.style.visibility = 'hidden';
  hiddenElement.style.whiteSpace = 'nowrap';
  hiddenElement.textContent = text;
  hiddenElement.classList.add('modal__table__cell');

  document.body.appendChild(hiddenElement);
  const width = hiddenElement.offsetWidth;
  document.body.removeChild(hiddenElement);

  return width;
};

/**
 * 列幅を最も大きいものに合わせて計算する関数
 * @param widthList - 各値の列幅
 */
const calculateColumnWidth = (widthList: number[]) => Math.max(0, ...widthList);

watch(
  () => props.state.isOpenResultRegisteredAnnounceModal,
  () => {
    if (props.state.isOpenResultRegisteredAnnounceModal) {
      const sumAmountColumnWidth = calculateColumnWidth(
        props.state.resultRegisteredAnnounces.flatMap((business) =>
          business.sumAmountMonthly.concat(business.sumAmountDaily).map((sumAmount) => calculateMinWidth(sumAmount.addComma()))
        )
      );
      const supplierNameColumnWidth = calculateColumnWidth(
        props.state.resultRegisteredAnnounces.map((business) => calculateMinWidth(business.supplierName))
      );
      const spacing = 20;
      maxTableNumberColumnWidthValue.value = `${sumAmountColumnWidth + spacing}px`;
      maxTableSupplierNameColumnWidthValue.value = `${supplierNameColumnWidth + spacing}px`;
    }
  }
);
</script>
<template>
  <InfoModal
    :modal-state="state.isOpenResultRegisteredAnnounceModal"
    :next-button-label="t('次へ')"
    :enable-outside-click="false"
    @on-next="emits('onNext')"
  >
    <template #title>{{ t('内示登録結果') }}</template>
    <template #description>
      <p>{{ t('登録に成功した内示数量の結果です。') }}</p>
    </template>
    <div class="modal__container">
      <div v-if="state.importFlags['内示']" class="modal__section">
        <p class="modal__section-title">{{ t('月別内示') }}</p>
        <table class="modal__table">
          <tr class="modal__table__row">
            <th class="modal__table__header">{{ t('仕入れ先名') }}</th>
            <th v-for="(monthText, index) in monthTexts" :key="`monthly-${index}-header`" class="modal__table__header">
              {{ monthText }}
            </th>
          </tr>
          <tr v-for="business in state.resultRegisteredAnnounces" :key="`monthly-${business.supplierName}`" class="modal__table__row">
            <td class="modal__table__cell">
              {{ business.supplierName }}
            </td>
            <td
              v-for="(sumAmount, index) in business.sumAmountMonthly"
              :key="`monthly-${index}-data`"
              class="modal__table__cell modal__table__cell--number"
            >
              {{ sumAmount.addComma() }}
            </td>
          </tr>
        </table>
      </div>
      <div v-if="state.importFlags['日別内示']" class="modal__section">
        <p class="modal__section-title">{{ t('日別内示') }}</p>
        <table class="modal__table">
          <tr class="modal__table__row">
            <th class="modal__table__header">{{ t('仕入れ先名') }}</th>
            <th v-for="(monthText, index) in monthTexts" :key="`daily-${index}-header`" class="modal__table__header">
              {{ monthText }}
            </th>
          </tr>
          <tr v-for="business in state.resultRegisteredAnnounces" :key="`daily-${business.supplierName}`" class="modal__table__row">
            <td class="modal__table__cell">
              {{ business.supplierName }}
            </td>
            <td
              v-for="(sumAmount, index) in business.sumAmountDaily"
              :key="`daily-${index}-data`"
              class="modal__table__cell modal__table__cell--number"
            >
              {{ sumAmount.addComma() }}
            </td>
          </tr>
        </table>
      </div>
    </div>
  </InfoModal>
</template>
<style lang="scss" scoped>
.modal {
  &__container {
    display: grid;
    gap: 16px;
    padding: 16px;
    background-color: $surfaceDefault;
  }

  &__section-title {
    margin: 0 0 8px;
    font-family: Noto Sans JP;
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    color: $textSecondary;
  }

  &__table {
    // border-spacing: 24px 0;
    // border-collapse: initial;

    &__header {
      font-family: Noto Sans JP;
      font-size: 14px;
      font-weight: 400;
      line-height: 18.2px;
      text-align: left;
      text-underline-position: from-font;
      text-decoration-skip-ink: none;
    }
    &__cell {
      background-color: $semanticWhite;
      color: $textSecondary;

      font-family: Noto Sans JP;
      font-size: 14px;
      font-weight: 400;
      line-height: 20.27px;
      text-align: left;
      text-underline-position: from-font;
      text-decoration-skip-ink: none;
      white-space: nowrap;

      padding: 0 8px;

      &--number {
        text-align: right;
      }
    }
    &__row {
      display: grid;
      grid-template-columns: v-bind(maxTableSupplierNameColumnWidthValue) repeat(
          v-bind(DISPLAY_FORECAST_PERIOD),
          v-bind(maxTableNumberColumnWidthValue)
        );
      gap: 24px;
    }
  }
}
</style>
